import { useState, useCallback, useRef } from 'react';
import type { Stroke, StrokeInput, Point, ChannelConfig } from '@twitch-draw/shared';
import { calculateBitsCost } from '@twitch-draw/shared';

const BITS_TIERS = [100, 300, 500, 1000, 2000, 5000];

const getTierPrice = (rawCost: number): number => {
  for (const tier of BITS_TIERS) {
    if (rawCost <= tier) {
      return tier;
    }
  }
  return BITS_TIERS[BITS_TIERS.length - 1];
};

export type AppState = 'loading' | 'drawing' | 'confirming' | 'processing';

interface UseDrawingStateOptions {
  initialStrokes?: Stroke[];
  channelId?: string;
  userId?: string;
  channelConfig?: ChannelConfig | null;
}

const DEFAULT_CONFIG: ChannelConfig = {
  bitsPerPx: 0.002,
  displayDurationMs: 30000,
  minBits: 100,
  maxBits: 5000,
};

export function useDrawingState(options: UseDrawingStateOptions = {}) {
  const { initialStrokes = [], channelId = 'unknown', userId = 'unknown', channelConfig } = options;
  const config = channelConfig ?? DEFAULT_CONFIG;

  const [appState, setAppState] = useState<AppState>('drawing');
  const [strokes, setStrokes] = useState<Stroke[]>(initialStrokes);
  const [pendingStrokes, setPendingStrokes] = useState<StrokeInput[]>([]);
  const [currentStroke, setCurrentStroke] = useState<StrokeInput | null>(null);
  const [totalCost, setTotalCost] = useState(0);

  const [color, setColor] = useState('#ff0055');
  const [width, setWidth] = useState(6);
  const [isEraser, setIsEraser] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const displayDuration = config.displayDurationMs;

  const startTimeRef = useRef<number>(0);
  const pointsRef = useRef<Point[]>([]);
  const pendingStrokesRef = useRef<StrokeInput[]>([]);

  pendingStrokesRef.current = pendingStrokes;

  const handleEraseAtPoint = useCallback((point: Point) => {
    const threshold = 0.025;
    setPendingStrokes(prev => {
      let changed = false;
      const newStrokes: StrokeInput[] = [];

      for (const stroke of prev) {
        const remainingPoints: Point[] = [];
        let currentSegment: Point[] = [];

        for (const p of stroke.points) {
          const dx = p[0] - point[0];
          const dy = p[1] - point[1];
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < threshold) {
            changed = true;
            if (currentSegment.length >= 2) {
              remainingPoints.push(...currentSegment);
              newStrokes.push({
                ...stroke,
                points: [...currentSegment],
              });
            }
            currentSegment = [];
          } else {
            currentSegment.push(p);
          }
        }

        if (currentSegment.length >= 2) {
          newStrokes.push({
            ...stroke,
            points: [...currentSegment],
          });
        }
      }

      return changed ? newStrokes : prev;
    });
  }, []);

  const handleStartStroke = useCallback((point: Point) => {
    if (isEraser) {
      handleEraseAtPoint(point);
      return;
    }
    startTimeRef.current = Date.now();
    pointsRef.current = [point];
    setCurrentStroke({
      color,
      width,
      durationMs: 0,
      points: [point],
    });
  }, [color, width, isEraser, handleEraseAtPoint]);

  const handleContinueStroke = useCallback((point: Point) => {
    if (isEraser) {
      handleEraseAtPoint(point);
      return;
    }
    if (!startTimeRef.current) return;
    pointsRef.current.push(point);
    const durationMs = Date.now() - startTimeRef.current;
    setCurrentStroke({
      color,
      width,
      durationMs,
      points: [...pointsRef.current],
    });
  }, [color, width, isEraser, handleEraseAtPoint]);

  const handleEndStroke = useCallback(() => {
    if (!startTimeRef.current || pointsRef.current.length < 2) {
      startTimeRef.current = 0;
      pointsRef.current = [];
      setCurrentStroke(null);
      return;
    }

    const durationMs = Date.now() - startTimeRef.current;
    const stroke: StrokeInput = {
      color,
      width,
      durationMs,
      points: [...pointsRef.current],
    };

    setPendingStrokes(prev => [...prev, stroke]);
    startTimeRef.current = 0;
    pointsRef.current = [];
    setCurrentStroke(null);
  }, [color, width]);

  const handleSubmit = useCallback(() => {
    if (pendingStrokes.length === 0) return;

    let rawTotal = 0;
    for (const stroke of pendingStrokes) {
      rawTotal += calculateBitsCost(stroke, 1920, 1080, config);
    }

    setTotalCost(getTierPrice(rawTotal));
    setAppState('confirming');
  }, [pendingStrokes, config]);

  const handleCancelPayment = useCallback(() => {
    setTotalCost(0);
    setAppState('drawing');
  }, []);

  const handleClearPending = useCallback(() => {
    setPendingStrokes([]);
  }, []);

  const handleEraserToggle = useCallback(() => {
    setIsEraser(true);
  }, []);

  const handleEraserOff = useCallback(() => {
    setIsEraser(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setShowToolbar(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowToolbar(false);
  }, []);

  const allStrokes: Stroke[] = [
    ...strokes,
    ...pendingStrokes.map((s, i) => ({
      id: `pending-${i}`,
      channelId,
      userId,
      createdAt: Date.now(),
      ...s,
    })),
  ];

  const rawPendingCost = pendingStrokes.reduce(
    (acc, s) => acc + calculateBitsCost(s, 1920, 1080, config),
    0
  );
  const pendingCost = getTierPrice(rawPendingCost);

  return {
    appState,
    setAppState,
    strokes,
    setStrokes,
    pendingStrokes,
    setPendingStrokes,
    currentStroke,
    totalCost,
    setTotalCost,
    color,
    setColor,
    width,
    setWidth,
    isEraser,
    showToolbar,
    displayDuration,
    pendingStrokesRef,
    handleStartStroke,
    handleContinueStroke,
    handleEndStroke,
    handleSubmit,
    handleCancelPayment,
    handleClearPending,
    handleEraserToggle,
    handleEraserOff,
    handleMouseEnter,
    handleMouseLeave,
    allStrokes,
    pendingCost,
  };
}
